import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loader from "../Common/Loader";
import { SitePage } from "../../Models/SitePage";
import SitePageApi from "../../Api/SitePageApi";
import FormApi from "../../Api/FormApi";
import { ShowcaseBoothMap } from "../../Models/ShowcaseBoothMap";
import BoothFloorplanViewer from "../Forms/BoothFloorplanViewer";
import { BoothAvailability } from "../../Models/BoothRegion";

const SHOWCASE_SITE_PAGE_ID = 14;
const POLL_MS = 60000;

type IndustryShowcasePageProps = {
  isPublic?: boolean;
};

export default function IndustryShowcasePage({
  isPublic = false,
}: IndustryShowcasePageProps) {
  const [pageData, setPageData] = useState<SitePage>();
  const [boothMap, setBoothMap] = useState<ShowcaseBoothMap | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);

  const loadBoothMap = useCallback(async () => {
    try {
      setMapLoading(true);
      const res = await FormApi.getShowcaseBoothMap();
      setBoothMap(res.data);
    } catch (err) {
      console.error("Failed to load showcase booth map", err);
      setBoothMap(null);
    } finally {
      setMapLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadPage = async () => {
      try {
        setIsLoading(true);
        if (isPublic) {
          const res = await SitePageApi.getPublicSitePage(SHOWCASE_SITE_PAGE_ID);
          setPageData(res.data);
        } else {
          const res = await SitePageApi.getSitePage(String(SHOWCASE_SITE_PAGE_ID));
          setPageData(res.data);
        }
      } catch (err) {
        console.error("Failed to load Industry Showcase page", err);
      } finally {
        setIsLoading(false);
      }
    };
    loadPage();
    loadBoothMap();
  }, [isPublic, loadBoothMap]);

  useEffect(() => {
    const id = setInterval(loadBoothMap, POLL_MS);
    return () => clearInterval(id);
  }, [loadBoothMap]);

  const assignments: BoothAvailability[] =
    boothMap?.assignments.map((a) => ({
      boothNumber: a.boothNumber,
      companyName: a.companyName,
      submittedDate: a.submittedDate,
      responseId: a.responseId,
    })) ?? [];

  const hasFloorplan = Boolean(boothMap?.floorplanUrl);
  const hasBoothPicker = Boolean(boothMap?.fieldId);
  const formId = boothMap?.formId;

  // Members must go to the Industry Showcase form, not Fall Convention registration.
  // (Fall Convention is a separate form; booth signup is formId from the showcase map.)
  const registerHref = formId
    ? isPublic
      ? `/submitform/${formId}`
      : `/forms/fill/${formId}`
    : isPublic
      ? "/Login"
      : "/members/industryshowcase";

  const pageTitle =
    pageData?.header ?? boothMap?.eventName ?? "Industry Showcase";

  return (
    <>
      <title>{pageData?.title ?? "Industry Showcase"}</title>
      <div className="w-full">
        <div className="w-full p-4">
          <div className="mx-1 flex items-end">
            <div className="flex flex-1">
              <h5 className="text-gray-900 font-bold text-4xl">{pageTitle}</h5>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-8">
          {isLoading && <Loader />}

          {pageData?.description ? (
            <div
              dangerouslySetInnerHTML={{
                __html: pageData.description,
              }}
            />
          ) : isPublic && !isLoading ? (
            <p className="text-gray-600">
              View live booth assignments for the EDMA Industry Showcase.{" "}
              <Link to="/Login" className="text-blue-700 underline font-medium">
                Sign in
              </Link>{" "}
              for full member content.
            </p>
          ) : null}

          <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-2xl font-bold text-gray-900">
                Booth assignments
              </h2>
              {formId ? (
                <Link
                  to={registerHref}
                  className="inline-flex items-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
                >
                  Register for a booth
                </Link>
              ) : null}
            </div>

            {!hasBoothPicker && !mapLoading && (
              <p className="text-amber-700 text-sm">
                Booth assignments are not configured yet. An administrator
                must add a BoothPicker field to the Industry Showcase
                registration form.
              </p>
            )}

            {hasBoothPicker && !hasFloorplan && (
              <p className="text-amber-700 text-sm">
                The booth map floorplan has not been uploaded yet.
              </p>
            )}

            {hasFloorplan && boothMap && (
              <BoothFloorplanViewer
                floorplanUrl={boothMap.floorplanUrl!}
                booths={boothMap.booths}
                assignments={assignments}
                loading={mapLoading}
                defaultPanelOpen
                lastRefreshedUtc={boothMap.lastRefreshedUtc}
              />
            )}

            {hasBoothPicker &&
              hasFloorplan &&
              assignments.length === 0 &&
              !mapLoading && (
                <p className="text-gray-600 text-sm">
                  No booth reservations yet. Check back after registration
                  opens.
                </p>
              )}
          </section>
        </div>
      </div>
    </>
  );
}
